/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationCreateEditForm Component
 * --------------------------
 * A fully featured form for creating new Applications within the
 * platform. Handles user input, validation, lookups, and submission.
 *
 * Key Features:
 * Fetches available Domains and Categories dynamically via API.
 * Provides real-time form validation for all required fields.
 * Displays contextual error messages and helper text.
 * On successful creation, automatically retrieves and displays the Client ID and Client Secret in a secure dialog.
 * Integrates themed inputs, buttons, dialogs, and progress bars.
 *
 * Technical Details:
 * Uses ThemeContext for consistent styling across components.
 * Uses APIHelper for backend communication and token handling.
 * Utilizes AlertDialogContext for confirmation and status messages.
 * Navigates to the applications dashboard upon successful creation or user-initiated cancellation.
 *
 * Props:
 * onSubmit (function): Callback triggered after successful creation.
 *
 * Returns:
 * A complete, themed form UI to create and register a new application.
 */

import React, { useState, useEffect } from "react";
import { useTheme } from "../theme/ThemeContext";
import GradientBar from "./common/GradientBar";
import ThemedInput from "./common/ThemedInput";
import ThemedButton from "./common/ThemedButton";
import LookupDialog from "./common/LookupDialog";
import FieldWrapper from "./common/FieldWrapper";
import RadioGroup from "./common/RadioGroup";
import ThemedTextArea from "./common/ThemedTextArea";
import ProgressBarDialog from "./common/ProgressBar";
import { useAlertDialog } from "./common/AlertDialogContext";
import { handleErrorResponse } from "../utils/utils";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import { useNavigate } from 'react-router-dom';
import { retrieve } from "../utils/storageHelper";
import ClientKeySecretDialog from "./common/ClientKeySecretDialog";
import FileUploadInput from "./common/FileUploadInput";
import ThemedToggle from "./common/ThemedToggle";
import { useLocation } from "react-router-dom"
import { useQuery } from "@tanstack/react-query";
import AlertDialog from './common/AlertDialog';
import { useParams } from "react-router-dom";

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

/**
 * Form is meant for Application Creation.
 * Manages form state for inputs like name, description, domain, category, status, banner image, and logo.
 * @returns 
 */
export default function ApplicationCreateEditForm({ onSubmit }) {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();
    const location = useLocation();
    const passedApp = location.state?.app || null;
    const isEdit = location.pathname.includes("/update");
    const { id } = useParams();

    const [cksOpen, setClientKeySecretOpen] = useState(false);
    const [clientKey, setClientKey] = useState("");
    const [clientSecret, setClientSecret] = useState("");

    const [domains, setDomains] = useState([]);
    const [categories, setCategories] = useState([]);

    const [domainError, setDomainError] = useState(null);
    const [categoryError, setCategoryError] = useState(null);

    const STATUS_CHOICES = isEdit
        ? [
            { value: "draft", label: "Draft" },
            { value: "published", label: "Published" }
        ]
        : [
            { value: "draft", label: "Draft" },
            { value: "published", label: "Published", disabled: true }
        ];
    const [form, setForm] = useState({
        name: "",
        description: "",
        status: "draft",
        domain: "",
        category: "",
        banner_image: "",
        logo: ""
    });

    const {
        data: appData,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ["application", id],
        enabled: isEdit && !passedApp && !!id,  // only when needed
        retry: 0,
        staleTime: Infinity,   // prevents refetch
        cacheTime: Infinity,   // never removed from cache
        queryFn: async ({ signal }) => {
            const resp = await apiHelper.apiCall(
                `/arcee/application/details/${id}/`,
                { method: "GET", signal }
            );

            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                const e = new Error("Failed to load application");
                e.info = errData;
                throw e;
            }

            return resp.json();
        },
    });

    useEffect(() => {
        const src = passedApp || appData;
        if (!src) return;

        setForm({
            name: src.name || src.application_name || "",
            description: src.description || "",
            status: src.status || "draft",
            domain: src.domain?.id || src.domain_id || "",
            category: src.category?.id || src.category_id || "",
            banner_image: src.banner_image || "",
            logo: src.logo || src.icon || ""
        });
    }, [passedApp, appData]);

    const domainsQueryKey = ['entities', 'domain'];
    const categoriesQueryKey = ['entities', 'category'];

    const {
        data: domainsQueryData,
        error: domainsQueryError,
        isLoading: domainsQueryLoading,
        isFetching: domainsQueryFetching,
    } = useQuery({
        queryKey: domainsQueryKey,
        queryFn: async ({ signal }) => {
            const url = `/arcee/entity/list/?entity_type=domain&page_size=5000`;
            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
            if (!resp.ok) {
                let j = {};
                try { j = await resp.json(); } catch (e) { /* ignore */ }
                throw new Error(j?.error || `Failed to fetch domains (${resp.status})`);
            }
            return await resp.json(); // { results: [...], count: N }
        },
        enabled: true,
        staleTime: 60_000,
        retry: 1,
    });

    const {
        data: categoriesQueryData,
        error: categoriesQueryError,
        isLoading: categoriesQueryLoading,
        isFetching: categoriesQueryFetching,
    } = useQuery({
        queryKey: categoriesQueryKey,
        queryFn: async ({ signal }) => {
            const url = `/arcee/entity/list/?entity_type=category&page_size=5000`;
            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });
            if (!resp.ok) {
                let j = {};
                try { j = await resp.json(); } catch (e) { /* ignore */ }
                throw new Error(j?.error || `Failed to fetch categories (${resp.status})`);
            }
            return await resp.json(); // { results: [...], count: N }
        },
        enabled: true,
        staleTime: 60_000,
        retry: 1,
    });

    // For banner image upload
    const handleBannerImageChange = (url) => {
        setForm(prev => ({ ...prev, banner_image: url }));
    }

    // For logo upload
    const handleLogoChange = (url) => {
        setForm(prev => ({ ...prev, logo: url }));
    }

    const [errors, setErrors] = useState({});
    const [domainDialogOpen, setDomainDialogOpen] = useState(false);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    /**
     * Shows the Application Client ID and Secret.
     */
    async function getApplicationCredentials(clientKey, clientSecret) {
        setClientKey(clientKey);
        setClientSecret(clientSecret);
        setClientKeySecretOpen(true);
    }

    /**
     * Handles closing the ClientKeySecretDialog and navigates back to applications list.
     */
    async function handleClose() {
        setClientKeySecretOpen(false);
        navigate('/dashboard/applications');
    }

    useEffect(() => {
        // domains
        if (domainsQueryError) {
            setDomainError(domainsQueryError?.message || String(domainsQueryError));
            setDomains([]);
        } else if (domainsQueryData) {
            setDomains(domainsQueryData.results || []);
            setDomainError(null);
        }

        // categories
        if (categoriesQueryError) {
            setCategoryError(categoriesQueryError?.message || String(categoriesQueryError));
            setCategories([]);
        } else if (categoriesQueryData) {
            setCategories(categoriesQueryData.results || []);
            setCategoryError(null);
        }
    }, [domainsQueryData, domainsQueryError, categoriesQueryData, categoriesQueryError]);

    if (!passedApp && isLoading) {
        return (
            <ProgressBarDialog
                open={true}
                text_message="Loading application..."
            />
        );
    }

    if (isError) {
        return (
            <AlertDialog
                open={true}
                type="error"
                title={error?.message || "Failed to load application"}
                message={(error?.info && error.info.error) || "Could not load application."}
                okLabel="Close"
                showCancel={false}
                onOk={() => navigate(`/dashboard/applications/${id}`)}
            />
        );
    }

    function validate() {
        const errs = {};
        if (!form.name.trim())
            errs.name = "Name is required";
        else if (!/^[a-zA-Z0-9 ]+$/.test(form.name))
            errs.name = "Only alphanumeric characters and spaces allowed";
        else if (form.name.length > 255)
            errs.name = "Max 255 characters allowed";

        if (!form.description.trim())
            errs.description = "Description is required";
        else if (form.description.length > 2000)
            errs.description = "Max 2000 characters allowed";

        if (!form.domain)
            errs.domain = "Domain is required";

        if (!form.category)
            errs.category = "Category is required";

        if (!form.status || !["draft", "published"].includes(form.status))
            errs.status = "Status is required";

        return errs;
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
        // Clear specific error when user types
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    }

    function handleLookup(type, value) {
        setForm({ ...form, [type]: value });
        if (type === "domain") {
            setDomainDialogOpen(false);
            if (errors.domain) setErrors({ ...errors, domain: null });
        }
        if (type === "category") {
            setCategoryDialogOpen(false);
            if (errors.category) setErrors({ ...errors, category: null });
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length === 0) {
            try {
                setLoading(true);

                const cleanValue = (value) => {
                    const trimmed = value.trim();
                    return trimmed === "" ? null : trimmed;
                };

                const payload = {
                    ...form,
                    logo: cleanValue(form.logo),
                    banner_image: cleanValue(form.banner_image),
                    application_id: id,
                    create_settings_credentials: true
                };

                const url = isEdit ? `/arcee/application/update/` : `/arcee/application/create/`;
                const method = isEdit ? 'PATCH' : 'POST'

                const resp = await apiHelper.apiCall(url, {
                    method: method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!resp.ok) await handleErrorResponse(resp);

                const data = await resp.json();

                const clientKey = data.client_id
                const clientSecret = data.client_secret
                showAlertDialog({
                    title: "Success",
                    message: isEdit ? "Application updated..." : "Application created, Please click on Continue to see Application Credentials.",
                    type: "success",
                    okLabel: "Continue",
                    showCancel: false,
                    onOk: () => {
                        if (isEdit) navigate(`/dashboard/applications/${id}`, { state: { force_refresh: true } });
                        else getApplicationCredentials(clientKey, clientSecret);
                    }
                });
                if (onSubmit) onSubmit(data);

            } catch (error) {
                showAlertDialog({
                    title: isEdit ? "Could not update application" : "Could not create application",
                    message: error.message,
                    type: "error",
                    okLabel: "OK",
                    showCancel: false,
                    onOk: () => {
                        setErrors({});
                    }
                });
            } finally {
                setLoading(false);
            }
        }
    }

    function handleCancel(e) {
        e.preventDefault();
        showAlertDialog({
            title: "Cancel",
            message: isEdit ? "Update application Cancelled" : "Create application Cancelled",
            type: "info",
            okLabel: "OK",
            showCancel: false,
            onOk: () => {
                if (isEdit) { navigate(`/dashboard/applications/${id}`); }
                else {
                    { navigate('/dashboard/applications'); }
                }
            }
        });
    }

    // --- UI Helpers ---
    const SectionTitle = ({ title, subtitle }) => (
        <div style={{ marginBottom: 24, borderBottom: `1px solid ${theme.borderColor || '#eee'}`, paddingBottom: 12 }}>
            <h3 style={{
                margin: 0,
                color: theme.text,
                fontSize: "1.1rem",
                fontWeight: 600,
                fontFamily: theme.fontFamily
            }}>{title}</h3>
            {subtitle && <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: 4 }}>{subtitle}</div>}
        </div>
    );

    return (
        <div style={{
            width: "100%",
            maxWidth: 900,
            margin: "40px auto",
            padding: "0 20px",
            fontFamily: theme.fontFamily,
            animation: "fadeIn 0.4s ease-in-out"
        }}>
            <ClientKeySecretDialog
                open={cksOpen}
                onClose={handleClose}
                clientKey={clientKey}
                clientSecret={clientSecret}
                canRegenerate={false}
                onRegenerate={null}
                actionLabel="OK"
            />
            {/* Simple Fade In Animation */}
            <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>

            <GradientBar height={8} />
            <ProgressBarDialog open={loading} text_message={isEdit ? "Updating Application..." : "Creating Application..."} cancellable={false} />

            <form
                style={{
                    background: "#fff",
                    borderRadius: theme.cardRadius,
                    boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
                    padding: "24px", // Reduced from 40px for better mobile fit
                    color: theme.text,
                    border: `1px solid ${theme.borderColor || '#f0f0f0'}`
                }}
                onSubmit={handleSubmit}
                noValidate
                autoComplete="off"
            >
                <div style={{ marginBottom: 32 }}>
                    <h2 style={{
                        color: theme.primary,
                        fontFamily: theme.fontFamily,
                        margin: "0 0 8px 0",
                        fontSize: "1.75rem",
                        fontWeight: 700
                    }}>{isEdit ? "Update Application" : "Create Application"}</h2>
                    <p style={{ margin: 0, color: "#64748b", fontSize: "0.95rem" }}>
                        {isEdit ? "Modify your application details below." : "Register a new application to start managing resources."}
                    </p>
                </div>

                {/* Section 1: Basic Details */}
                <SectionTitle title="Basic Details" subtitle="Enter the core identity of your application." />

                <div style={{ marginBottom: 24 }}>
                    <FieldWrapper label="Application Name" required error={errors.name}>
                        <ThemedInput
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            maxLength={255}
                            autoFocus
                            pattern="[a-zA-Z0-9 ]+"
                            title="Alphanumeric and spaces only"
                            error={!!errors.name}
                            placeholder="e.g. Finance Portal"
                            style={{ fontSize: "1rem", padding: "10px 12px" }}
                        />
                    </FieldWrapper>
                </div>

                <div style={{ marginBottom: 24 }}>
                    <FieldWrapper label="Description" required error={errors.description}>
                        <ThemedTextArea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            maxLength={2000}
                            rows={4}
                            error={!!errors.description}
                            placeholder="Briefly describe what this application does..."
                            style={{ fontSize: "0.95rem", padding: "10px 12px", minHeight: 100 }}
                        />
                        <div style={{ color: "#94a3b8", fontSize: 11, textAlign: "right", marginTop: 4 }}>
                            {form.description.length}/2000
                        </div>
                    </FieldWrapper>
                </div>

                {/* Section 2: Classification */}
                <div style={{ marginTop: 40 }}>
                    <SectionTitle title="Classification" subtitle="Organize your application under specific domains and categories." />

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "24px", marginBottom: 24 }}>
                        <FieldWrapper label="Domain" required error={!!errors.domain}>
                            <div className="input-group" style={{ display: "flex", alignItems: "stretch", boxShadow: "0 2px 5px rgba(0,0,0,0.03)", borderRadius: theme.buttonRadius }}>
                                <ThemedInput
                                    name="domain"
                                    value={domains.find(d => d.id === Number(form.domain))?.name || ""}
                                    readOnly
                                    error={!!errors.domain}
                                    placeholder="Select Domain"
                                    onClick={() => setDomainDialogOpen(true)}
                                    style={{
                                        cursor: "pointer",
                                        background: "#f8fafc",
                                        borderRight: "none",
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        fontSize: "0.95rem",
                                        minWidth: 0,
                                        flex: 1
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setDomainDialogOpen(true)}
                                    style={{
                                        border: `1.4px solid ${errors.domain ? "#d53b3b" : theme.primary}`,
                                        borderLeft: "none",
                                        borderTopRightRadius: theme.buttonRadius,
                                        borderBottomRightRadius: theme.buttonRadius,
                                        background: theme.secondary,
                                        color: "#fff",
                                        width: 48,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "background 0.2s"
                                    }}
                                >
                                    <i className="bi bi-search" style={{ fontSize: "1.1rem" }} />
                                </button>
                            </div>
                            <LookupDialog
                                open={domainDialogOpen}
                                title="Select Domain"
                                options={domains}
                                onSelect={(id) => handleLookup("domain", id)}
                                onClose={() => setDomainDialogOpen(false)}
                            />
                            {domainError && <div style={{ color: "#d53b3b", fontSize: "0.85rem", marginTop: 4 }}>{domainError}</div>}
                        </FieldWrapper>

                        <FieldWrapper label="Category" required error={!!errors.category}>
                            <div className="input-group" style={{ display: "flex", alignItems: "stretch", boxShadow: "0 2px 5px rgba(0,0,0,0.03)", borderRadius: theme.buttonRadius }}>
                                <ThemedInput
                                    name="category"
                                    value={categories.find(c => c.id === Number(form.category))?.name || ""}
                                    readOnly
                                    error={!!errors.category}
                                    placeholder="Select Category"
                                    onClick={() => setCategoryDialogOpen(true)}
                                    style={{
                                        cursor: "pointer",
                                        background: "#f8fafc",
                                        borderRight: "none",
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        fontSize: "0.95rem",
                                        minWidth: 0,
                                        flex: 1
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setCategoryDialogOpen(true)}
                                    style={{
                                        border: `1.4px solid ${errors.category ? "#d53b3b" : theme.primary}`,
                                        borderLeft: "none",
                                        borderTopRightRadius: theme.buttonRadius,
                                        borderBottomRightRadius: theme.buttonRadius,
                                        background: theme.secondary,
                                        color: "#fff",
                                        width: 48,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        transition: "background 0.2s"
                                    }}
                                >
                                    <i className="bi bi-search" style={{ fontSize: "1.1rem" }} />
                                </button>
                            </div>
                            <LookupDialog
                                open={categoryDialogOpen}
                                title="Select Category"
                                options={categories}
                                onSelect={(id) => handleLookup("category", id)}
                                onClose={() => setCategoryDialogOpen(false)}
                            />
                            {categoryError && <div style={{ color: "#d53b3b", fontSize: "0.85rem", marginTop: 4 }}>{categoryError}</div>}
                        </FieldWrapper>
                    </div>

                    <div style={{ marginBottom: 24, maxWidth: "50%", minWidth: 200 }} className="status-container">
                        <FieldWrapper label="Status" required error={!!errors.status}>
                            <div style={{
                                padding: "12px",
                                border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                borderRadius: theme.cardRadius,
                                background: "#f8fafc"
                            }}>
                                <ThemedToggle
                                    checked={form.status === "published"}
                                    onChange={(val) => handleChange({ target: { name: "status", value: val ? "published" : "draft" } })}
                                    onLabel="Published"
                                    offLabel="Draft"
                                    disabled={!isEdit}
                                />
                            </div>
                            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: "0.85rem" }}>
                                <i className="bi bi-info-circle" />
                                <span>{isEdit ? "Toggle to publish or draft application." : "New applications are created as Draft."}</span>
                            </div>
                        </FieldWrapper>
                    </div>
                </div>

                {/* Section 3: Branding */}
                <div style={{ marginTop: 40 }}>
                    <SectionTitle title="Branding" subtitle="Customize the visual appearance (Optional)." />

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "32px" }}>
                        <FieldWrapper label="Banner Image">
                            <div style={{ padding: 4, background: "#fafafa", borderRadius: 8, border: "1px dashed #cbd5e1" }}>
                                <FileUploadInput
                                    is_public={true}
                                    value={form.banner_image}
                                    onChange={handleBannerImageChange}
                                    allowedFileTypes={["image"]}
                                    inputProps={{ name: "banner_image" }}
                                    expectedImageWidth={750}
                                    expectedImageHeight={150}
                                    hint="JPG/PNG, Max 1MB, 750x150px"
                                    uploadTooltip="Upload Banner"
                                />
                            </div>
                        </FieldWrapper>

                        <FieldWrapper label="Logo">
                            <div style={{ padding: 4, background: "#fafafa", borderRadius: 8, border: "1px dashed #cbd5e1" }}>
                                <FileUploadInput
                                    is_public={true}
                                    value={form.logo}
                                    onChange={handleLogoChange}
                                    allowedFileTypes={["image"]}
                                    inputProps={{ name: "logo" }}
                                    expectedImageWidth={512}
                                    expectedImageHeight={512}
                                    hint="JPG/PNG, Max 1MB, 512x512px"
                                    uploadTooltip="Upload Logo"
                                />
                            </div>
                        </FieldWrapper>
                    </div>
                </div>

                {/* Actions */}
                <div className="action-buttons">
                    <ThemedButton
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={loading}
                        style={{
                            padding: "10px 24px",
                            fontSize: "0.95rem",
                            borderWidth: "1.5px"
                        }}
                    >
                        Cancel
                    </ThemedButton>

                    <ThemedButton
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: "10px 32px",
                            fontSize: "0.95rem",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                        }}
                    >
                        {loading ? "Processing..." : (isEdit ? "Update Application" : "Create Application")}
                    </ThemedButton>
                </div>

                {/* Responsive Styles Injection */}
                <style>{`
                    .action-buttons {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 48px;
                        gap: 16px;
                        padding-top: 24px;
                        border-top: 1px solid ${theme.borderColor || '#f0f0f0'};
                    }
                    @media (max-width: 480px) {
                        .action-buttons {
                            flex-direction: column;
                            gap: 12px;
                        }
                        .action-buttons button {
                            width: 100%;
                            justify-content: center;
                        }
                        .status-container {
                            max-width: 100% !important;
                        }
                        h3 { font-size: 1rem !important; }
                    }
                    /* Extra Small Screens (< 430px) - Flush Layout */
                    @media (max-width: 430px) {
                        form {
                            box-shadow: none !important;
                            border: none !important;
                            background: transparent !important;
                            padding: 0 !important;
                        }
                        /* Ensure inputs still look good against transparent background (or assume body is white/light) */
                        /* If body is grey, this might look odd, but user asked to remove "White section with shadow" */
                    }
                `}</style>
            </form>
        </div>
    );
}
